import React from "react";
import {
  Switch as SwitchContext,
  Redirect,
  Route,
  withRouter,
  RouteComponentProps,
} from "react-router-dom";
import { observer } from "mobx-react";

import AuthStore from "stores/AuthStore";

import Login from "pages/Login";
import ForgotPassword from "pages/ForgotPassword";
import Today from "pages/InApp/Today";
import TodayDetails from "pages/InApp/Today/TodayDetails";
import QuickLinkDetails from "pages/InApp/Today/QuickLinkDetails";
import DayBanners from "pages/InApp/Today/DayBanners";
import InAppStudio from "pages/InApp/Studio";
import InAppNutrition from "pages/InApp/Nutrition/InAppNutrition";
import InAppResources from "pages/InApp/Resources/InAppResources";
import NutritionCategoryDetails from "pages/InApp/Nutrition/NutritionCategoryDetails";
import NutritionCategoryRecipeDetails from "pages/InApp/Nutrition/NutritionCategoryDetails/RecipeDetails";
import CategoryDetails from "pages/InApp/Studio/CategoryDetails";
import CategoryWorkoutDetails from "pages/InApp/Studio/CategoryDetails/WorkoutDetials";
import ResourcesCategoryDetails from "pages/InApp/Resources/ResourcesCategoryDetails";
import ResourcesQuickLinkDetails from "pages/InApp/Resources/ResourcesCategoryDetails/QuickLinkDetails";
import ContentNutrition from "pages/Content/Nutrition/ContentNutrition";
import ContentStudio from "pages/Content/Studio";
import ContentResources from "pages/Content/Resources/ContentResources";
import Tags from "pages/Content/Tags";
import SettingsUsers from "pages/Settings/Users";
import Questionnaire from "pages/Content/Questionnaire";
import CoachResources from "pages/Content/CoachResources";
import Profile from "pages/Content/Profile";
import ContentTips from "pages/Content/Tips/ContentTips";
import TipDetails from "pages/Content/Tips/Tips/TipDetails";
import Pattern from "pages/Content/Pattern/ContentPattern";
import Trivia from "pages/Content/Trivia";
import TriviaDetails from "pages/Content/Trivia/TriviaDetails";
import Claims from "pages/Content/Claims";
import ClaimDetails from "pages/Content/Claims/ClaimDetails";
import ActiveProgram from "pages/Content/ActiveProgram";
import ActiveProgramDetails from "pages/Content/ActiveProgram/ActiveProgramDetails";
import Restrictions from "pages/Content/Restrictions";
import RestrictionDetails from "pages/Content/Restrictions/RestrictionDetails";
import HealthConditions from "pages/Content/HealthConditions";
import HealthConditionDetails from "pages/Content/HealthConditions/HealthConditionDetails";

import GlobalNavigationContext from "./context";
import ROUTES from "./routes";
import Greetings from "../pages/Content/Greetings";
import TargetZone from "../pages/Content/TargetZone";
import TargetZoneDetails from "../pages/Content/TargetZone/TargetZoneDetails";
import Trainer from "../pages/Content/Trainer";
import TrainerDetails from "../pages/Content/Trainer/TrainerDetails";
import InAppManualBadges from "../pages/InApp/ManualBadges/ManualBadges";
import InAppWorkoutSequences from "../pages/InApp/WorkoutSequences/WorkoutSequences";
import InAppManualBadgeDetails from "../pages/InApp/ManualBadges/ManualBadgeDetails/ManualBadgeDetails";
import WorkoutSequenceDetails from "pages/InApp/Studio/WorkoutSequenceDetails/WorkoutSequenceDetails";
import Collections from "pages/Content/Collections/Collections";
import AddEdit from "pages/Content/Collections/AddEdit/AddEdit";

type Props = {
  component: React.ComponentClass<any> | React.FC<any>;
  allowAccess: boolean;
  redirectTo: string;
  path: string | string[];
  exact?: boolean;
};

export const PrivateRoute = (props: Props) => {
  const {
    component: Component,
    allowAccess,
    redirectTo,
    path,
    ...rest
  } = props;

  return (
    <Route
      {...rest}
      render={props =>
        allowAccess || path === ROUTES.LOGIN ? (
          <Component {...props} />
        ) : (
          <Redirect
            to={{
              pathname: allowAccess ? ROUTES.LOGIN : redirectTo,
              state: { from: props.location.pathname },
            }}
          />
        )
      }
    />
  );
};

@observer
class Switch extends React.Component<RouteComponentProps> {
  get navigationContext() {
    return {
      navigateToLogin: this.navigateToLogin,
    };
  }

  navigateToLogin = () => {
    this.props.history.push(ROUTES.LOGIN);
  };

  renderRedirectIfUserIsLoggedIn = () =>
    AuthStore.isAuthorized ? (
      <Redirect to={ROUTES.IN_APP_STUDIO} />
    ) : (
      <Redirect to={ROUTES.LOGIN} />
    );

  render() {
    return (
      <GlobalNavigationContext.Provider value={this.navigationContext}>
        <SwitchContext>
          <Route exact path="/" render={this.renderRedirectIfUserIsLoggedIn} />
          <Route exact path={ROUTES.LOGIN} component={Login} />
          <Route
            exact
            path={ROUTES.FORGOT_PASSWORD}
            component={ForgotPassword}
          />

          <PrivateRoute
            path={ROUTES.IN_APP_TODAY}
            component={Today}
            allowAccess={AuthStore.isAuthorized}
            redirectTo={ROUTES.LOGIN}
            exact
          />
          <PrivateRoute
            path={[
              // ROUTES.IN_APP_TODAY_NUTRITION,
              ROUTES.IN_APP_TODAY_QUICK_LINKS,
            ]}
            component={TodayDetails}
            allowAccess={AuthStore.isAuthorized}
            redirectTo={ROUTES.LOGIN}
            exact
          />
          <PrivateRoute
            path={ROUTES.IN_APP_TODAY_BANNERS}
            component={DayBanners}
            allowAccess={AuthStore.isAuthorized}
            redirectTo={ROUTES.LOGIN}
            exact
          />
          <PrivateRoute
            path={[
              // ROUTES.IN_APP_TODAY_NUTRITION_QUICK_LINK_DETAILS,
              ROUTES.IN_APP_TODAY_QUICK_LINKS_LINK_DETAILS,
            ]}
            component={QuickLinkDetails}
            allowAccess={AuthStore.isAuthorized}
            redirectTo={ROUTES.LOGIN}
            exact
          />
          <PrivateRoute
            path={ROUTES.IN_APP_NUTRITION}
            component={InAppNutrition}
            allowAccess={AuthStore.isAuthorized}
            redirectTo={ROUTES.LOGIN}
            exact
          />
          <PrivateRoute
            path={ROUTES.IN_APP_NUTRITION_CATEGORY_DETAILS}
            component={NutritionCategoryDetails}
            allowAccess={AuthStore.isAuthorized}
            redirectTo={ROUTES.LOGIN}
            exact
          />
          <PrivateRoute
            path={ROUTES.IN_APP_NUTRITION_CATEGORY_DETAILS_RECIPE_DETAILS}
            component={NutritionCategoryRecipeDetails}
            allowAccess={AuthStore.isAuthorized}
            redirectTo={ROUTES.LOGIN}
            exact
          />
          <PrivateRoute
            path={ROUTES.IN_APP_STUDIO}
            component={InAppStudio}
            allowAccess={AuthStore.isAuthorized}
            redirectTo={ROUTES.LOGIN}
            exact
          />
          <PrivateRoute
            path={ROUTES.IN_APP_STUDIO_CATEGORY_DETAILS}
            component={CategoryDetails}
            allowAccess={AuthStore.isAuthorized}
            redirectTo={ROUTES.LOGIN}
            exact
          />
          <PrivateRoute
            path={ROUTES.IN_APP_STUDIO_WORKOUT_SEQUENCE_DETAILS}
            component={WorkoutSequenceDetails}
            allowAccess={AuthStore.isAuthorized}
            redirectTo={ROUTES.LOGIN}
            exact
          />

          <PrivateRoute
            path={ROUTES.IN_APP_STUDIO_CATEGORY_DETAILS_WORKOUT_DETAILS}
            component={CategoryWorkoutDetails}
            allowAccess={AuthStore.isAuthorized}
            redirectTo={ROUTES.LOGIN}
            exact
          />
          <PrivateRoute
            path={ROUTES.IN_APP_RESOURCES}
            component={InAppResources}
            allowAccess={AuthStore.isAuthorized}
            redirectTo={ROUTES.LOGIN}
            exact
          />
          <PrivateRoute
            path={ROUTES.IN_APP_RESOURCES_CATEGORY_DETAILS}
            component={ResourcesCategoryDetails}
            allowAccess={AuthStore.isAuthorized}
            redirectTo={ROUTES.LOGIN}
            exact
          />
          <PrivateRoute
            path={ROUTES.IN_APP_RESOURCES_CATEGORY_DETAILS_QUICK_LINK_DETAILS}
            component={ResourcesQuickLinkDetails}
            allowAccess={AuthStore.isAuthorized}
            redirectTo={ROUTES.LOGIN}
            exact
          />
          <PrivateRoute
            path={ROUTES.IN_APP_WORKOUT_SEQUENCES}
            component={InAppWorkoutSequences}
            allowAccess={AuthStore.isAuthorized}
            redirectTo={ROUTES.LOGIN}
            exact
          />
          <PrivateRoute
            path={ROUTES.IN_APP_MANUAL_BADGES}
            component={InAppManualBadges}
            allowAccess={AuthStore.isAuthorized}
            redirectTo={ROUTES.LOGIN}
            exact
          />
          <PrivateRoute
            path={ROUTES.IN_APP_MANUAL_BADGES_DETAILS}
            component={InAppManualBadgeDetails}
            allowAccess={AuthStore.isAuthorized}
            redirectTo={ROUTES.LOGIN}
            exact
          />

          <PrivateRoute
            path={ROUTES.CONTENT_NUTRITION}
            component={ContentNutrition}
            allowAccess={AuthStore.isAuthorized}
            redirectTo={ROUTES.LOGIN}
          />
          <PrivateRoute
            path={ROUTES.CONTENT_STUDIO}
            component={ContentStudio}
            allowAccess={AuthStore.isAuthorized}
            redirectTo={ROUTES.LOGIN}
          />
          <PrivateRoute
            path={ROUTES.CONTENT_RESOURCES}
            component={ContentResources}
            allowAccess={AuthStore.isAuthorized}
            redirectTo={ROUTES.LOGIN}
          />
          <PrivateRoute
            path={ROUTES.CONTENT_TIPS_LINK_DETAILS}
            component={TipDetails}
            allowAccess={AuthStore.isAuthorized}
            redirectTo={ROUTES.LOGIN}
            exact
          />
          <PrivateRoute
            path={ROUTES.CONTENT_TAGS}
            component={Tags}
            allowAccess={AuthStore.isAuthorized}
            redirectTo={ROUTES.LOGIN}
            exact
          />
          <PrivateRoute
            path={ROUTES.CONTENT_QUESTIONNAIRE}
            component={Questionnaire}
            allowAccess={AuthStore.isAuthorized}
            redirectTo={ROUTES.LOGIN}
          />
          <PrivateRoute
            path={ROUTES.CONTENT_PATTERNS}
            component={Pattern}
            allowAccess={AuthStore.isAuthorized}
            redirectTo={ROUTES.LOGIN}
          />
          <PrivateRoute
            path={ROUTES.CONTENT_TIPS}
            component={ContentTips}
            allowAccess={AuthStore.isAuthorized}
            redirectTo={ROUTES.LOGIN}
          />
          <PrivateRoute
            path={ROUTES.CONTENT_CLAIMS}
            component={Claims}
            allowAccess={AuthStore.isAuthorized}
            redirectTo={ROUTES.LOGIN}
            exact
          />
          <PrivateRoute
            path={ROUTES.CONTENT_CLAIMS_DETAILS}
            component={ClaimDetails}
            allowAccess={AuthStore.isAuthorized}
            redirectTo={ROUTES.LOGIN}
            exact
          />
          <PrivateRoute
            path={ROUTES.CONTENT_CONTENT_COLLECTIONS}
            component={Collections}
            allowAccess={AuthStore.isAuthorized}
            redirectTo={ROUTES.LOGIN}
            exact
          />
          <PrivateRoute
            path={ROUTES.CONTENT_ADD_EDIT_COLLECTION}
            component={AddEdit}
            allowAccess={AuthStore.isAuthorized}
            redirectTo={ROUTES.LOGIN}
            exact
          />
          <PrivateRoute
            path={ROUTES.CONTENT_ACTIVE_PROGRAM}
            component={ActiveProgram}
            allowAccess={AuthStore.isAuthorized}
            redirectTo={ROUTES.LOGIN}
            exact
          />
          <PrivateRoute
            path={ROUTES.CONTENT_ACTIVE_PROGRAM_DETAILS}
            component={ActiveProgramDetails}
            allowAccess={AuthStore.isAuthorized}
            redirectTo={ROUTES.LOGIN}
            exact
          />
          <PrivateRoute
            path={ROUTES.CONTENT_GREETINGS}
            component={Greetings}
            allowAccess={AuthStore.isAuthorized}
            redirectTo={ROUTES.LOGIN}
            exact
          />
          <PrivateRoute
            path={ROUTES.CONTENT_TARGET_ZONES}
            component={TargetZone}
            allowAccess={AuthStore.isAuthorized}
            redirectTo={ROUTES.LOGIN}
            exact
          />
          <PrivateRoute
            path={ROUTES.CONTENT_TARGET_ZONES_DETAILS}
            component={TargetZoneDetails}
            allowAccess={AuthStore.isAuthorized}
            redirectTo={ROUTES.LOGIN}
            exact
          />
          <PrivateRoute
            path={ROUTES.CONTENT_TRAINERS}
            component={Trainer}
            allowAccess={AuthStore.isAuthorized}
            redirectTo={ROUTES.LOGIN}
            exact
          />
          <PrivateRoute
            path={ROUTES.CONTENT_TRAINERS_DETAILS}
            component={TrainerDetails}
            allowAccess={AuthStore.isAuthorized}
            redirectTo={ROUTES.LOGIN}
            exact
          />
          <PrivateRoute
            path={ROUTES.CONTENT_RESTRICTION}
            component={Restrictions}
            allowAccess={AuthStore.isAuthorized}
            redirectTo={ROUTES.LOGIN}
            exact
          />
          <PrivateRoute
            path={ROUTES.CONTENT_RESTRICTION_DETAILS}
            component={RestrictionDetails}
            allowAccess={AuthStore.isAuthorized}
            redirectTo={ROUTES.LOGIN}
            exact
          />
          <PrivateRoute
            path={ROUTES.CONTENT_HEALTH_CONDITION}
            component={HealthConditions}
            allowAccess={AuthStore.isAuthorized}
            redirectTo={ROUTES.LOGIN}
            exact
          />
          <PrivateRoute
            path={ROUTES.CONTENT_HEALTH_CONDITION_DETAILS}
            component={HealthConditionDetails}
            allowAccess={AuthStore.isAuthorized}
            redirectTo={ROUTES.LOGIN}
            exact
          />
          <PrivateRoute
            path={ROUTES.CONTENT_TRIVIA}
            component={Trivia}
            allowAccess={AuthStore.isAuthorized}
            redirectTo={ROUTES.LOGIN}
            exact
          />
          <PrivateRoute
            path={ROUTES.CONTENT_TRIVIA_DETAIL}
            component={TriviaDetails}
            allowAccess={AuthStore.isAuthorized}
            redirectTo={ROUTES.LOGIN}
            exact
          />

          <PrivateRoute
            path={ROUTES.CONTENT_PERSONAL}
            component={CoachResources}
            allowAccess={AuthStore.isAuthorized}
            redirectTo={ROUTES.LOGIN}
          />
          <PrivateRoute
            path={ROUTES.CONTENT_PROFILE}
            component={Profile}
            allowAccess={AuthStore.isAuthorized}
            redirectTo={ROUTES.LOGIN}
          />

          <PrivateRoute
            path={ROUTES.SETTINGS_USERS}
            component={SettingsUsers}
            allowAccess={AuthStore.isAuthorized}
            redirectTo={ROUTES.LOGIN}
          />

          <Redirect to={ROUTES.LOGIN} />
        </SwitchContext>
      </GlobalNavigationContext.Provider>
    );
  }
}

export default withRouter(Switch);
